import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Wrench,
  LogOut,
  Shield,
  CheckCircle2,
  Eye,
  Clock,
  MessageSquare,
  ChevronDown,
  Save,
  X,
  Loader2,
  Image,
} from "lucide-react";
import { Link } from "wouter";
import { format } from "date-fns";
import type { ContactRequest, ContactStatus } from "@shared/schema";
import { useAuth } from "@/hooks/useAuth";
import Login from "./Login";
import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";

type StatusInfo = {
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
};

const statusConfig: Record<ContactStatus, StatusInfo> = {
  new: {
    label: "New",
    icon: <Clock className="h-3.5 w-3.5" />,
    color: "text-amber-400",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/30",
  },
  read: {
    label: "Read",
    icon: <Eye className="h-3.5 w-3.5" />,
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
  },
  in_progress: {
    label: "In Progress",
    icon: <Loader2 className="h-3.5 w-3.5" />,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
  },
  resolved: {
    label: "Resolved",
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    borderColor: "border-emerald-500/30",
  },
};

export default function Admin() {
  const { user, isAuthenticated, isLoading: authLoading, logout, isLoggingOut } = useAuth();
  const queryClient = useQueryClient();
  const [notesDialogOpen, setNotesDialogOpen] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState("");

  const { data: requests, isLoading } = useQuery<ContactRequest[]>({
    queryKey: ["/api/contact"],
    enabled: isAuthenticated,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { status?: ContactStatus; notes?: string } }) => {
      return apiRequest("PATCH", `/api/contact/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contact"] });
    },
  });

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Error handling is done in the hook
    }
  };

  const handleStatusChange = (id: string, status: ContactStatus) => {
    updateMutation.mutate({ id, data: { status } });
  };

  const handleSaveNotes = (id: string) => {
    updateMutation.mutate({ id, data: { notes: editingNotes } });
    setNotesDialogOpen(null);
    setEditingNotes("");
  };

  const openNotesDialog = (request: ContactRequest) => {
    setEditingNotes(request.notes || "");
    setNotesDialogOpen(request.id);
  };

  const getServiceLabel = (service: string) => {
    const serviceMap: Record<string, string> = {
      bathroom: "Bathroom Renovation",
      kitchen: "Kitchen Remodeling",
      renovation: "Home Renovation",
      handyman: "Handyman Services",
      woodwork: "Custom Woodwork",
    };
    return serviceMap[service] || service;
  };

  const getStatusInfo = (status: string): StatusInfo => {
    return statusConfig[status as ContactStatus] || statusConfig.new;
  };

  // Count requests by status
  const statusCounts = requests?.reduce((acc, req) => {
    const status = req.status || "new";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
          <p className="mt-4 text-gray-400">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-cyan-500/20 glass-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Link href="/">
                <Button variant="ghost" size="icon" data-testid="button-back-home" className="hover:bg-cyan-500/10">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <h1 className="text-3xl font-heading font-bold gradient-text" data-testid="text-admin-title">
                  Contact Requests
                </h1>
                <p className="text-muted-foreground" data-testid="text-admin-subtitle">
                  Manage and review customer inquiries
                </p>
              </div>
            </div>
            
            {/* User info and actions */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Shield className="h-4 w-4 text-cyan-400" />
                <span>Logged in as <span className="text-cyan-400 font-medium">{user?.username}</span></span>
              </div>
              <a href="/cms">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-purple-500/30 hover:bg-purple-500/10 hover:border-purple-500/50 text-purple-400"
                >
                  <span className="flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    Media Library
                  </span>
                </Button>
              </a>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="border-cyan-500/30 hover:bg-cyan-500/10 hover:border-cyan-500/50"
              >
                {isLoggingOut ? (
                  <span className="flex items-center gap-2">
                    <span className="h-3 w-3 border-2 border-t-transparent border-current rounded-full animate-spin" />
                    Logging out...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogOut className="h-4 w-4" />
                    Logout
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {isLoading ? (
          <div className="text-center py-12" data-testid="text-loading">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
            <p className="mt-4 text-muted-foreground">Loading requests...</p>
          </div>
        ) : !requests || requests.length === 0 ? (
          <Card className="p-12 text-center glass-card border-cyan-500/20" data-testid="card-no-requests">
            <Wrench className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h2 className="text-2xl font-heading font-semibold mb-2 gradient-text">No Requests Yet</h2>
            <p className="text-muted-foreground">
              Contact requests will appear here when customers submit the form
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Stats row */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <p className="text-sm text-muted-foreground" data-testid="text-request-count">
                Total Requests: <span className="font-semibold text-cyan-400">{requests.length}</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(statusConfig).map(([status, info]) => (
                  <div
                    key={status}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${info.bgColor} ${info.color} border ${info.borderColor}`}
                  >
                    {info.icon}
                    <span>{info.label}: {statusCounts[status] || 0}</span>
                  </div>
                ))}
              </div>
            </div>

            {requests.map((request) => {
              const statusInfo = getStatusInfo(request.status || "new");
              
              return (
                <Card
                  key={request.id}
                  className={`p-6 glass-card border-cyan-500/20 hover-lift hover-glow-cyan transition-all ${
                    request.status === "new" ? "border-l-4 border-l-amber-400" : ""
                  }`}
                  data-testid={`card-request-${request.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div>
                            <h3 className="text-xl font-heading font-semibold mb-1 text-gray-100" data-testid={`text-request-name-${request.id}`}>
                              {request.name}
                            </h3>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="border-cyan-500/30 text-cyan-400" data-testid={`badge-service-${request.id}`}>
                                {getServiceLabel(request.service)}
                              </Badge>
                              {/* Status Badge with Dropdown */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`h-6 px-2 gap-1 ${statusInfo.bgColor} ${statusInfo.color} border ${statusInfo.borderColor} hover:opacity-80`}
                                    disabled={updateMutation.isPending}
                                  >
                                    {statusInfo.icon}
                                    <span className="text-xs">{statusInfo.label}</span>
                                    <ChevronDown className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="glass-card border-cyan-500/20">
                                  {Object.entries(statusConfig).map(([status, info]) => (
                                    <DropdownMenuItem
                                      key={status}
                                      onClick={() => handleStatusChange(request.id, status as ContactStatus)}
                                      className={`gap-2 cursor-pointer ${info.color}`}
                                    >
                                      {info.icon}
                                      <span>{info.label}</span>
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-muted-foreground" data-testid={`text-request-date-${request.id}`}>
                            {format(new Date(request.createdAt), "MMM d, yyyy 'at' h:mm a")}
                          </div>
                          {request.readAt && (
                            <div className="text-xs text-gray-500 mt-1">
                              Read: {format(new Date(request.readAt), "MMM d, h:mm a")}
                            </div>
                          )}
                          {request.resolvedAt && (
                            <div className="text-xs text-emerald-500 mt-1">
                              Resolved: {format(new Date(request.resolvedAt), "MMM d, h:mm a")}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-2" data-testid={`text-request-email-${request.id}`}>
                          <Mail className="h-4 w-4 text-cyan-400" />
                          <a href={`mailto:${request.email}`} className="hover:text-cyan-400 transition-colors">
                            {request.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2" data-testid={`text-request-phone-${request.id}`}>
                          <Phone className="h-4 w-4 text-cyan-400" />
                          <a href={`tel:${request.phone}`} className="hover:text-cyan-400 transition-colors">
                            {request.phone}
                          </a>
                        </div>
                        {request.preferredDate && (
                          <div className="flex items-center gap-2" data-testid={`text-request-preferred-date-${request.id}`}>
                            <Calendar className="h-4 w-4 text-gold-500" />
                            <span>Preferred: {format(new Date(request.preferredDate), "MMM d, yyyy")}</span>
                          </div>
                        )}
                      </div>

                      <div className="bg-cyan-500/5 border border-cyan-500/10 p-4 rounded-md">
                        <p className="text-sm font-medium mb-1 text-cyan-400">Project Description:</p>
                        <p className="text-sm leading-relaxed text-gray-300" data-testid={`text-request-message-${request.id}`}>
                          {request.message}
                        </p>
                      </div>

                      {/* Notes Section */}
                      <div className="flex items-start gap-2">
                        <Dialog open={notesDialogOpen === request.id} onOpenChange={(open) => {
                          if (!open) {
                            setNotesDialogOpen(null);
                            setEditingNotes("");
                          }
                        }}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-1.5 border-cyan-500/30 hover:bg-cyan-500/10 hover:border-cyan-500/50"
                              onClick={() => openNotesDialog(request)}
                            >
                              <MessageSquare className="h-3.5 w-3.5" />
                              {request.notes ? "Edit Notes" : "Add Notes"}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="glass-card border-cyan-500/20 sm:max-w-[500px]">
                            <DialogHeader>
                              <DialogTitle className="gradient-text">Notes for {request.name}</DialogTitle>
                              <DialogDescription>
                                Add internal notes about this contact request. Notes are only visible to admins.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="py-4">
                              <Textarea
                                value={editingNotes}
                                onChange={(e) => setEditingNotes(e.target.value)}
                                placeholder="Add your notes here..."
                                className="min-h-[150px] bg-background/50 border-cyan-500/20 focus:border-cyan-500/50"
                              />
                            </div>
                            <DialogFooter className="gap-2">
                              <Button
                                variant="ghost"
                                onClick={() => {
                                  setNotesDialogOpen(null);
                                  setEditingNotes("");
                                }}
                                className="gap-1.5"
                              >
                                <X className="h-4 w-4" />
                                Cancel
                              </Button>
                              <Button
                                onClick={() => handleSaveNotes(request.id)}
                                disabled={updateMutation.isPending}
                                className="gap-1.5 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700"
                              >
                                {updateMutation.isPending ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4" />
                                )}
                                Save Notes
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>

                        {request.notes && (
                          <div className="flex-1 bg-gray-800/50 border border-gray-700/50 rounded-md p-3">
                            <p className="text-xs font-medium text-gray-400 mb-1 flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              Notes
                            </p>
                            <p className="text-sm text-gray-300 whitespace-pre-wrap">{request.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
